import type { ComponentType } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import App from "@/src/App";
import HomeRedirect from "@/src/pages/HomeRedirect";

const lazyPage = (loader: () => Promise<{ default: ComponentType }>) => ({
  lazy: () => loader().then((module) => ({ Component: module.default })),
});

export const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    children: [
      { index: true, element: <HomeRedirect /> },
      { path: "splash", ...lazyPage(() => import("@/app/splash/page")) },
      { path: "landing", ...lazyPage(() => import("@/app/landing/page")) },
      { path: "login", element: <Navigate to="/login/email" replace /> },
      { path: "login/email", ...lazyPage(() => import("@/app/login/email/page")) },
      { path: "signup", ...lazyPage(() => import("@/app/signup/page")) },
      { path: "signup/property", ...lazyPage(() => import("@/app/signup/property/page")) },
      { path: "verify-email", ...lazyPage(() => import("@/app/verify-email/page")) },
      { path: "forgot-password", element: <Navigate to="/login/email" replace /> },
      { path: "forgot-password/sent", element: <Navigate to="/login/email" replace /> },
      { path: "forgot-password/reset", element: <Navigate to="/login/email" replace /> },
      { path: "dashboard", ...lazyPage(() => import("@/app/dashboard/page")) },
      { path: "health-score", ...lazyPage(() => import("@/app/health-score/page")) },
      { path: "alerts", ...lazyPage(() => import("@/app/alerts/page")) },
      { path: "forecast", ...lazyPage(() => import("@/app/forecast/page")) },
      { path: "maintenance", ...lazyPage(() => import("@/app/maintenance/page")) },
      { path: "assets", ...lazyPage(() => import("@/app/assets/page")) },
      { path: "assets/:id", ...lazyPage(() => import("@/app/assets/[id]/page")) },
      { path: "assets/:id/edit", ...lazyPage(() => import("@/app/assets/[id]/edit/page")) },
      { path: "assets/detail", ...lazyPage(() => import("@/app/assets/detail/page")) },
      {
        path: "assets/new",
        ...lazyPage(() => import("@/src/layouts/AssetWizardLayout")),
        children: [
          { index: true, element: <Navigate to="capture" replace /> },
          { path: "capture", ...lazyPage(() => import("@/app/assets/new/capture/page")) },
          { path: "review", ...lazyPage(() => import("@/app/assets/new/review/page")) },
        ],
      },
      { path: "scan", element: <Navigate to="/assets/new/capture" replace /> },
      { path: "scan/appliance", ...lazyPage(() => import("@/app/scan/appliance/page")) },
      { path: "scan-home", ...lazyPage(() => import("@/app/scan-home/page")) },
      { path: "properties", ...lazyPage(() => import("@/app/properties/page")) },
      { path: "properties/add", ...lazyPage(() => import("@/app/properties/add/page")) },
      {
        path: "properties/edit/:propertyId",
        ...lazyPage(() => import("@/app/properties/edit/[propertyId]/page")),
      },
      { path: "documents", ...lazyPage(() => import("@/app/documents/page")) },
      { path: "vendors", ...lazyPage(() => import("@/app/vendors/page")) },
      { path: "subscription", ...lazyPage(() => import("@/app/subscription/page")) },
      {
        path: "subscription/success",
        ...lazyPage(() => import("@/app/subscription/success/page")),
      },
      {
        path: "subscription/cancelled",
        ...lazyPage(() => import("@/app/subscription/cancelled/page")),
      },
      { path: "profile", ...lazyPage(() => import("@/app/profile/page")) },
      { path: "design-system", ...lazyPage(() => import("@/app/design-system/page")) },
      { path: "app-chooser", element: <Navigate to="/splash" replace /> },
      { path: "admin/*", element: <Navigate to="/splash" replace /> },
      { path: "*", element: <Navigate to="/assets" replace /> },
    ],
  },
]);
